"use client"

import { useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorBookingCta } from "@/components/tools/calculator-booking-cta"
import { buttonVariants } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"
import {
  defaultLipidUnit,
  fromMmolPerL,
  looksLikeMmolPerLEntry,
  offersLipidUnitSwitch,
  toMmolPerL,
  type LipidUnit,
} from "@/lib/lipid-units"
import { mafldCalculatorKeys as k } from "@/i18n/mafld-calculator-keys"
import { formatDecimal } from "@/lib/locale-number-format"
import { track } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import {
  DECLARED_CRITERIA,
  FLI_MAX,
  FLI_MIN,
  FLI_RULES_IN_FROM,
  FLI_RULES_OUT_BELOW,
  MAFLD_CRITERIA,
  MAFLD_LIMBS,
  MAFLD_OUTCOMES,
  MAX_GGT,
  MAX_HEIGHT_CM,
  MAX_TRIGLYCERIDES,
  MAX_WAIST_CM,
  MAX_WEIGHT_KG,
  MIN_GGT,
  MIN_HEIGHT_CM,
  MIN_TRIGLYCERIDES,
  MIN_WAIST_CM,
  MIN_WEIGHT_KG,
  SEX_OPTIONS,
  STEATOSIS_OPTIONS,
  TRIGLYCERIDE_MG_DL_PER_MMOL_L,
  assessMafld,
  mafldProblem,
  noDeclaredAnswers,
  type DeclaredAnswers,
  type FliBand,
  type MafldAssessment,
  type MafldOutcome,
  type MafldProblem,
  type MafldSex,
  type SteatosisAnswer,
} from "@/lib/mafld"

/**
 * The interactive part of /tools/mafld-calculator.
 *
 * One centred column: the page intro, then the form, then the result panel,
 * which does not exist until Calculate has run. One component owns all three
 * because the form and the result share state; the `intro` slot is what keeps
 * the h1 in the client file, where the page's structure is meant to stay
 * readable.
 *
 * Deliberately framer-motion-free: the widget is always mounted, so any
 * animation here would have to clear the `<AnimatePresence>` + cacheComponents
 * trap (see `/animation-cache-trap`). CSS transitions cover what it needs.
 *
 * Nothing is submitted, stored or tracked. That matters here more than on any
 * other calculator on the site: between them these answers are a body, a lipid
 * panel, a liver panel, a diagnosis and what a scan found, and the arithmetic
 * happens in the browser and never leaves it.
 *
 * What makes this widget different in shape from the other five is that the
 * output is a VERDICT, not a number. The Fatty Liver Index is on screen and
 * on the scale, but it is a means: the thing the reader came for is whether
 * the two halves of the definition hold, which of the three metabolic routes
 * carried them, and what to do next. So the result panel is built out of the
 * registries in `lib/mafld.ts` rather than a fixed set of paragraphs, and the
 * one thing it must never do is print a verdict the assessment did not reach.
 */

/**
 * The index axis drawn under the result: 0 to 100, the index's own range.
 *
 * Unlike the sMAF-5 and FIB-4 axes this needs no invented head-room, because
 * the Fatty Liver Index is a rescaled logistic and cannot leave its bounds.
 * Both cut-offs sit inside it at 30% and 60%.
 */
const SCALE_MIN = FLI_MIN
const SCALE_MAX = FLI_MAX

/** Exported for the index table's band chips in the client file. */
export const FLI_BAND_STYLES: Record<
  FliBand,
  { bar: string; chip: string; accent: string }
> = {
  unlikely: {
    bar: "bg-deep-green-400",
    chip: "bg-deep-green-100 text-deep-green-800",
    accent: "text-deep-green-600",
  },
  indeterminate: {
    bar: "bg-terracotta-300",
    chip: "bg-terracotta-100 text-terracotta-900",
    accent: "text-terracotta-600",
  },
  likely: {
    bar: "bg-terracotta-600",
    chip: "bg-terracotta-200 text-terracotta-950",
    accent: "text-terracotta-800",
  },
}

/**
 * Chip styles for the four verdicts.
 *
 * Not the same map as the index bands above, and the difference is the point.
 * A high index is drawn as the warning colour because a high index argues for
 * fat. A verdict is drawn on whether the definition is MET, which is a
 * different axis: `noMetabolic` carries a HIGH index and still is not MAFLD,
 * so colouring the chip off the index would print a red "Another cause" beside
 * a green explanation of it. Both `indeterminate` and `noMetabolic` are the
 * middle colour because both mean the same thing to the reader, which is that
 * the page has not finished the job and a doctor has to.
 */
export const OUTCOME_STYLES: Record<MafldOutcome, { chip: string }> = {
  met: { chip: "bg-terracotta-200 text-terracotta-950" },
  indeterminate: { chip: "bg-terracotta-100 text-terracotta-900" },
  noMetabolic: { chip: "bg-terracotta-100 text-terracotta-900" },
  noSteatosis: { chip: "bg-deep-green-100 text-deep-green-800" },
}

const inputClasses =
  "type-body-lg w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 tabular-nums focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-aubergine-400 focus-visible:outline-none"

const unitSuffixClasses =
  "type-body-md pointer-events-none absolute inset-y-0 right-4 flex items-center text-neutral-500"

const labelClasses = "type-label-md block text-neutral-700 normal-case"

const hintClasses = "type-body-sm mt-2 text-neutral-500"

const choiceClasses =
  "type-title-sm flex-1 cursor-pointer rounded-full px-4 py-2.5 transition-colors"

/**
 * The triglyceride field's two units, in the order they are offered: the
 * market's own convention first, since that is what the field starts on where
 * this control renders at all.
 *
 * `as const` and at module scope for the reason `SEX_OPTIONS` in lib/mafld.ts
 * documents: annotating `labelKey` as `string` widens it out of the catalog's
 * key union and next-intl's typed `t()` rejects it.
 */
const TG_UNIT_OPTIONS = [
  { value: "mgPerDl", labelKey: k.calcUnitMgDl },
  { value: "mmolPerL", labelKey: k.calcUnitMmol },
] as const satisfies readonly {
  value: LipidUnit
  labelKey: MessageKeys<"mafldCalculator">
}[]

/**
 * `as const`, and at module scope, for the reason `SEX_OPTIONS` in
 * `lib/mafld.ts` documents: annotating `labelKey` as `string` widens it out of
 * the catalog's key union and next-intl's typed `t()` rejects it.
 */
const DIABETES_OPTIONS = [
  { value: false, labelKey: k.calcDiabetesNo },
  { value: true, labelKey: k.calcDiabetesYes },
] as const

/**
 * NaN for a blank or unparseable field. `Number("")` is 0, which would read a
 * still-empty box as a real zero: an empty height field would submit as 0 cm,
 * and dividing by its square returns Infinity rather than anything the
 * plausibility check would catch as a typo.
 *
 * The comma swap is load-bearing on this page and not a nicety. Three of the
 * five numeric fields here are decimals a Swiss report prints with a comma
 * (1,8 mmol/L), the page itself writes them with a comma in French and German,
 * and `Number("1,8")` is NaN. Without this the submit button would sit
 * disabled with every box visibly filled in.
 */
function parseField(value: string): number {
  const trimmed = value.trim().replace(",", ".")
  if (trimmed === "") return NaN
  return Number(trimmed)
}

export function MafldCalculatorWidget({ intro }: { intro?: ReactNode }) {
  const t = useTranslations("mafldCalculator")
  // The index and the BMI are rendered here rather than coming from the
  // catalog, so the decimal separator has to be chosen explicitly: a French
  // page printing "83.6" beside prose that says "83,6" contradicts itself.
  const locale = useLocale()
  const fieldId = useId()

  /**
   * Flipped in an effect, and stamped onto the root as `data-hydrated`.
   *
   * Not decoration: the inputs are controlled, so anything typed into them
   * before React owns them is silently discarded, and an e2e test has no other
   * way to know when that moment has passed. On the BMI page a blind retry
   * loop kept flaking on a loaded CI runner until this signal existed. One
   * boolean, renders nothing.
   */
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  /**
   * The three questions start UNSET, rather than defaulting the way the BMR
   * page's sex toggle does. Each one would change the verdict silently if it
   * carried a default:
   *
   *   - diabetes satisfies the metabolic half on its own, at any weight, so a
   *     preselected "No" would push a lean reader with type 2 diabetes onto
   *     the third route and demand two markers they do not need.
   *   - the steatosis answer decides whether the verdict rests on the reader's
   *     own imaging or on an estimate, and a default would quietly discard a
   *     scan. Defaulting the other way, to "never looked", would be worse
   *     still: it would overwrite a scan that ruled fat OUT with an estimate
   *     that may rule it in.
   *   - sex sets both the waist and the HDL thresholds, and there is no
   *     defensible default for it.
   *
   * The cost is that the submit button stays disabled a moment longer, which
   * is the right trade: an unanswered question is visible, a wrong default is
   * not.
   */
  const [sex, setSex] = useState<MafldSex | null>(null)
  const [diabetes, setDiabetes] = useState<boolean | null>(null)
  const [steatosis, setSteatosis] = useState<SteatosisAnswer | null>(null)
  const [heightCm, setHeightCm] = useState("")
  const [weightKg, setWeightKg] = useState("")
  const [waistCm, setWaistCm] = useState("")
  const [triglycerides, setTriglycerides] = useState("")
  const [ggt, setGgt] = useState("")

  /**
   * The unit the triglyceride field asks for, and whether the reader can
   * change it.
   *
   * Both come from the market's laboratory convention (lib/lipid-units.ts):
   * German labs report lipids in mg/dL, Swiss ones in mmol/L, and the model
   * computes in mmol/L whatever the field says. Where the switch is offered
   * the field starts on the local convention, so the common case is copying a
   * number across unchanged.
   *
   * `useState`'s initialiser rather than an effect: the country is known on
   * the server, so this renders correct in the static HTML instead of
   * flipping after hydration.
   */
  const { code: country } = useCountry()
  const [tgUnit, setTgUnit] = useState<LipidUnit>(() =>
    defaultLipidUnit(country)
  )
  const canSwitchTgUnit = offersLipidUnitSwitch(country)

  /**
   * Switching the unit CLEARS the field rather than converting what is in it.
   *
   * Converting would be friendlier and is exactly the wrong thing here: the
   * reader would watch 159 become 1.795, and the next person to read this page
   * over their shoulder has no way to tell a converted value from a typed one.
   * An empty box asks for the number that is actually on their report, which
   * is the only number this page should ever compute from.
   */
  function selectTgUnit(next: LipidUnit) {
    if (next === tgUnit) return
    setTgUnit(next)
    setTriglycerides("")
    invalidate()
  }

  /**
   * The bounds and the placeholder in whichever unit is being asked for. The
   * `min`/`max` attributes have to follow the unit or the browser's stepper
   * walks a mg/dL entry straight out of the accepted range.
   */
  const tgMin = Math.round(
    fromMmolPerL(MIN_TRIGLYCERIDES, tgUnit, TRIGLYCERIDE_MG_DL_PER_MMOL_L)
  )
  const tgMax = Math.round(
    fromMmolPerL(MAX_TRIGLYCERIDES, tgUnit, TRIGLYCERIDE_MG_DL_PER_MMOL_L)
  )
  const tgStep = tgUnit === "mgPerDl" ? "1" : "0.1"
  const tgPlaceholder = tgUnit === "mgPerDl" ? "110" : "1.2"

  /**
   * The four ticked markers.
   *
   * Checkboxes, and so unticked by default, which is the one place this page
   * departs from the rule above. `lib/mafld.ts` explains why that is safe
   * (the error runs one way only) and `leanUnanswered` is what surfaces it.
   */
  const [declared, setDeclared] = useState<DeclaredAnswers>(noDeclaredAnswers)

  /**
   * The result only exists after the button, and any change to any answer
   * drops it. A stale verdict sitting beside edited inputs is the one thing a
   * side-by-side layout must not show, and it matters more here than on a BMI
   * page: this is a yes or no about a named condition.
   */
  const [result, setResult] = useState<MafldAssessment | null>(null)
  /**
   * `MafldProblem` plus one case the model cannot see: a mmol/L figure typed
   * while mg/dL is the selected unit. That one converts to a value inside
   * every range in lib/mafld.ts (1.8 becomes 0.02 mmol/L), so it has to be
   * caught here, where the selected unit is known.
   */
  const [problem, setProblem] = useState<
    MafldProblem | "triglyceridesMmolValue" | null
  >(null)

  /** Every setter on this page goes through this. */
  function invalidate() {
    setResult(null)
    setProblem(null)
  }

  function toggleDeclared(id: (typeof DECLARED_CRITERIA)[number]["id"]) {
    setDeclared((current) => ({ ...current, [id]: !current[id] }))
    invalidate()
  }

  const hasInput =
    sex !== null ||
    diabetes !== null ||
    steatosis !== null ||
    heightCm.trim() !== "" ||
    weightKg.trim() !== "" ||
    waistCm.trim() !== "" ||
    triglycerides.trim() !== "" ||
    ggt.trim() !== "" ||
    DECLARED_CRITERIA.some(({ id }) => declared[id])

  /**
   * The eight answers, or null while any one of them is unanswered.
   *
   * The four checkboxes are deliberately NOT in this gate. An unticked box is
   * a real answer, so requiring them would leave the button disabled for
   * every reader who meets none of the four, which is most of them.
   */
  const measurements = useMemo(() => {
    if (sex === null || diabetes === null || steatosis === null) return null
    const cm = parseField(heightCm)
    const kg = parseField(weightKg)
    const waist = parseField(waistCm)
    const tg = parseField(triglycerides)
    const ggtValue = parseField(ggt)
    if (
      !Number.isFinite(cm) ||
      !Number.isFinite(kg) ||
      !Number.isFinite(waist) ||
      !Number.isFinite(tg) ||
      !Number.isFinite(ggtValue)
    ) {
      return null
    }
    return {
      sex,
      diabetes,
      steatosis,
      heightCm: cm,
      weightKg: kg,
      waistCm: waist,
      // The one converted input on this page. Everything in lib/mafld.ts is
      // mmol/L, so the market's unit stops here.
      triglycerides: toMmolPerL(tg, tgUnit, TRIGLYCERIDE_MG_DL_PER_MMOL_L),
      ggt: ggtValue,
      declared,
    }
  }, [
    sex,
    diabetes,
    steatosis,
    heightCm,
    weightKg,
    waistCm,
    triglycerides,
    tgUnit,
    ggt,
    declared,
  ])

  /**
   * Plausibility is checked here rather than by disabling the button: a
   * disabled control with no explanation leaves the reader guessing, so the
   * button enables as soon as every question is answered and an unassessable
   * entry gets the message that explains which of the three things went wrong.
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!measurements) return
    // Read against the RAW entry, not the converted one: what makes this a
    // mistake is the number the reader typed next to the unit they had
    // selected, and the conversion has already hidden that.
    if (
      looksLikeMmolPerLEntry(
        parseField(triglycerides),
        tgUnit,
        MIN_TRIGLYCERIDES,
        TRIGLYCERIDE_MG_DL_PER_MMOL_L
      )
    ) {
      setResult(null)
      setProblem("triglyceridesMmolValue")
      track.calculatorResult({
        calculator: "mafld",
        outcome: "rejected",
        reason: "triglyceridesMmolValue",
      })
      return
    }
    const found = mafldProblem(measurements)
    if (found !== null) {
      setResult(null)
      setProblem(found)
      track.calculatorResult({
        calculator: "mafld",
        outcome: "rejected",
        reason: found,
      })
      return
    }
    setProblem(null)
    track.calculatorResult({ calculator: "mafld", outcome: "result" })
    setResult(assessMafld(measurements))
  }

  function reset() {
    setSex(null)
    setDiabetes(null)
    setSteatosis(null)
    setHeightCm("")
    setWeightKg("")
    setWaistCm("")
    setTriglycerides("")
    setGgt("")
    setDeclared(noDeclaredAnswers())
    invalidate()
  }

  /**
   * Segment widths on the 0 to 100 axis, in index points. Fixed: the index has
   * one pair of cut-offs and they do not move with age, sex or anything else.
   */
  const segments = [
    // no-raw-ids: band lookup key, never rendered
    { id: "unlikely" as const, span: FLI_RULES_OUT_BELOW - SCALE_MIN },
    {
      // no-raw-ids: band lookup key, never rendered
      id: "indeterminate" as const,
      span: FLI_RULES_IN_FROM - FLI_RULES_OUT_BELOW,
    },
    // no-raw-ids: band lookup key, never rendered
    { id: "likely" as const, span: SCALE_MAX - FLI_RULES_IN_FROM },
  ]

  const positionOf = (value: number) =>
    Math.min(
      100,
      Math.max(0, ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100)
    )

  /**
   * The message for whatever went wrong, keyed by the selected unit where
   * that changes what is worth saying.
   *
   * `triglyceridesUnit` is the model's guard on a value above 30 mmol/L, and
   * it exists to name the mg/dL mistake. With mg/dL actually selected that
   * reading is wrong: 3000 mg/dL is not a unit confusion, it is a number
   * outside the range, so the plain out-of-range line is the honest one.
   */
  function describeProblem(): string | null {
    switch (problem) {
      case null:
        return null
      case "triglyceridesMmolValue":
        return t(k.calcTriglyceridesMmolValue)
      case "triglyceridesUnit":
        return tgUnit === "mmolPerL"
          ? t(k.calcTriglyceridesUnit)
          : t(k.calcOutOfRange)
      case "bodyOutOfRange":
        return t(k.calcBodyOutOfRange)
      default:
        return t(k.calcOutOfRange)
    }
  }

  const problemMessage = describeProblem()

  const fliStyles = result ? FLI_BAND_STYLES[result.fliBand] : null

  return (
    <div
      data-testid="mafld-calculator"
      data-hydrated={hydrated ? "true" : undefined}
      /* One centred column at the prose measure, lining up with the
         sections below it. The result no longer needs a column of its
         own: it does not exist until Calculate has run. */
      className="mx-auto w-full max-w-[760px]"
    >
      {/* ── Intro ── */}
      <div className="text-center">{intro}</div>

      {/* ── Form ── */}
      {/*
        `noValidate` is load-bearing, not a style choice.

        The number inputs below carry `min`/`max` so a phone shows a sensible
        keypad and a stepper cannot walk outside the accepted range. But those
        attributes also arm the browser's own constraint validation, which
        runs BEFORE `onSubmit` and cancels the submit outright on a violation.
        On the SCORE2 page that made a field look like a dead button:
        `handleSubmit` never ran, `problem` was never set, and the result
        panel sat on its empty-state line. A CI Playwright run caught it.

        It also has to be `noValidate` rather than dropping `min`/`max`,
        because the browser's bubble cannot say what these messages say.
        "Value must be less than or equal to 30" is the wrong thing to tell
        someone who entered their triglycerides in mg/dL: the number is not
        too large, it is in the wrong unit, and that is the one mistake on
        this page that would print "fat likely" over a clean liver.
      */}
      <form onSubmit={handleSubmit} noValidate className="mt-10">
        <h2 className="type-title-lg text-center text-neutral-900">
          {t(k.calcTitle)}
        </h2>

        <div className="mt-6 space-y-4">
          {/* Sex — a real radiogroup, styled as a segmented control. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-sex-label`}>
            <span id={`${fieldId}-sex-label`} className={labelClasses}>
              {t(k.calcSexLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {SEX_OPTIONS.map((option) => {
                const selected = sex === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`mafld-sex-${option.value}`}
                    onClick={() => {
                      setSex(option.value)
                      invalidate()
                    }}
                    className={cn(
                      choiceClasses,
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={`${fieldId}-height`} className={labelClasses}>
                {t(k.calcHeightLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-height`}
                  data-testid="mafld-height"
                  type="number"
                  inputMode="numeric"
                  min={MIN_HEIGHT_CM}
                  max={MAX_HEIGHT_CM}
                  step="1"
                  autoComplete="off"
                  placeholder="165"
                  value={heightCm}
                  onChange={(e) => {
                    setHeightCm(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-14")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitCm)}</span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-weight`} className={labelClasses}>
                {t(k.calcWeightLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-weight`}
                  data-testid="mafld-weight"
                  type="number"
                  inputMode="decimal"
                  min={MIN_WEIGHT_KG}
                  max={MAX_WEIGHT_KG}
                  step="0.1"
                  autoComplete="off"
                  placeholder="70"
                  value={weightKg}
                  onChange={(e) => {
                    setWeightKg(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-14")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitKg)}</span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcBodyHint)}</p>

          {/* Waist — the heaviest term in the index AND one of the six
              markers, which is why it gets its own row and its own hint
              rather than sitting beside the weight. */}
          <div>
            <label htmlFor={`${fieldId}-waist`} className={labelClasses}>
              {t(k.calcWaistLabel)}
            </label>
            <div className="relative mt-2 sm:max-w-[50%]">
              <input
                id={`${fieldId}-waist`}
                data-testid="mafld-waist"
                type="number"
                inputMode="decimal"
                min={MIN_WAIST_CM}
                max={MAX_WAIST_CM}
                step="0.5"
                autoComplete="off"
                placeholder="90"
                value={waistCm}
                onChange={(e) => {
                  setWaistCm(e.target.value)
                  invalidate()
                }}
                className={cn(inputClasses, "pr-14")}
              />
              <span className={unitSuffixClasses}>{t(k.calcUnitCm)}</span>
            </div>
            <p className={hintClasses}>{t(k.calcWaistHint)}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor={`${fieldId}-triglycerides`}
                  className={labelClasses}
                >
                  {t(k.calcTriglyceridesLabel)}
                </label>
                {/*
                  The unit the field asks for, where the market's labs and
                  the model disagree (lib/lipid-units.ts). A radiogroup
                  rather than a select: two options, and it has to be
                  obvious which one is active, because the whole point is
                  that the reader recognises their own report's unit.
                */}
                {canSwitchTgUnit && (
                  <div
                    role="radiogroup"
                    aria-label={t(k.calcTriglyceridesUnitLabel)}
                    className="flex gap-1"
                  >
                    {TG_UNIT_OPTIONS.map((option) => {
                      const selected = tgUnit === option.value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          data-testid={`mafld-tg-unit-${option.value}`}
                          onClick={() => selectTgUnit(option.value)}
                          className={cn(
                            "type-label-md cursor-pointer rounded-full px-3 py-1 normal-case transition-colors",
                            selected
                              ? "bg-aubergine-800 text-white"
                              : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                          )}
                        >
                          {t(option.labelKey)}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-triglycerides`}
                  data-testid="mafld-triglycerides"
                  data-unit={tgUnit}
                  type="number"
                  inputMode="decimal"
                  min={tgMin}
                  max={tgMax}
                  step={tgStep}
                  autoComplete="off"
                  placeholder={tgPlaceholder}
                  value={triglycerides}
                  onChange={(e) => {
                    setTriglycerides(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-20")}
                />
                <span className={unitSuffixClasses}>
                  {tgUnit === "mgPerDl" ? t(k.calcUnitMgDl) : t(k.calcUnitMmol)}
                </span>
              </div>
            </div>
            <div>
              <label htmlFor={`${fieldId}-ggt`} className={labelClasses}>
                {t(k.calcGgtLabel)}
              </label>
              <div className="relative mt-2">
                <input
                  id={`${fieldId}-ggt`}
                  data-testid="mafld-ggt"
                  type="number"
                  inputMode="decimal"
                  min={MIN_GGT}
                  max={MAX_GGT}
                  step="1"
                  autoComplete="off"
                  placeholder="25"
                  value={ggt}
                  onChange={(e) => {
                    setGgt(e.target.value)
                    invalidate()
                  }}
                  className={cn(inputClasses, "pr-16")}
                />
                <span className={unitSuffixClasses}>{t(k.calcUnitUl)}</span>
              </div>
            </div>
          </div>
          <p className={hintClasses}>{t(k.calcBloodHint)}</p>
          <p className={hintClasses}>
            {tgUnit === "mgPerDl"
              ? t(k.calcTriglyceridesHintMgDl)
              : t(k.calcTriglyceridesHint)}
          </p>

          {/* Diabetes — satisfies the metabolic half on its own, and unset
              until answered. See the state declaration above. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-diabetes-label`}>
            <span id={`${fieldId}-diabetes-label`} className={labelClasses}>
              {t(k.calcDiabetesLabel)}
            </span>
            <div className="mt-2 flex gap-2">
              {DIABETES_OPTIONS.map((option) => {
                const selected = diabetes === option.value
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`mafld-diabetes-${option.value ? "yes" : "no"}`}
                    onClick={() => {
                      setDiabetes(option.value)
                      invalidate()
                    }}
                    className={cn(
                      choiceClasses,
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
            <p className={hintClasses}>{t(k.calcDiabetesHint)}</p>
          </div>

          {/* Steatosis — three options, and the one that decides whether the
              verdict rests on the reader's imaging or on the index.
              `flex-col sm:flex-row` because the three labels do not fit
              across a phone the way a two-option control does. */}
          <div role="radiogroup" aria-labelledby={`${fieldId}-steatosis-label`}>
            <span id={`${fieldId}-steatosis-label`} className={labelClasses}>
              {t(k.calcSteatosisLabel)}
            </span>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              {STEATOSIS_OPTIONS.map((option) => {
                const selected = steatosis === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`mafld-steatosis-${option.value}`}
                    onClick={() => {
                      setSteatosis(option.value)
                      invalidate()
                    }}
                    className={cn(
                      choiceClasses,
                      "whitespace-nowrap",
                      selected
                        ? "bg-aubergine-800 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    )}
                  >
                    {t(option.labelKey)}
                  </button>
                )
              })}
            </div>
            <p className={hintClasses}>{t(k.calcSteatosisHint)}</p>
          </div>

          {/*
            The four ticked markers.

            Rendered for every reader rather than only for the lean ones they
            can decide, which is a deliberate trade. Showing them
            conditionally on a BMI that is still being typed would make the
            form jump under the reader's hands, and the four boxes are worth
            seeing either way: they are four of the six criteria the page's
            own table explains, and the result panel reports which of them a
            reader meets whichever route carried them. `calcMarkersHint` says
            in as many words when they count.

            Native checkboxes, styled: the label is the click target through
            `htmlFor`, and each one prints its own threshold so the tick is an
            informed one rather than a guess at what "raised" means.
          */}
          <fieldset className="rounded-[16px] bg-neutral-100 p-5">
            <legend className={cn(labelClasses, "px-1")}>
              {t(k.calcMarkersLabel)}
            </legend>
            <div className="mt-3 space-y-3">
              {DECLARED_CRITERIA.map((criterion) => (
                <div key={criterion.id} className="flex items-start gap-3">
                  <input
                    id={`${fieldId}-${criterion.id}`}
                    data-testid={`mafld-marker-${criterion.id}`}
                    type="checkbox"
                    checked={declared[criterion.id]}
                    onChange={() => toggleDeclared(criterion.id)}
                    className="accent-aubergine-800 mt-1 h-4 w-4 shrink-0 cursor-pointer"
                  />
                  <label
                    htmlFor={`${fieldId}-${criterion.id}`}
                    className="cursor-pointer"
                  >
                    <span className="type-title-sm block text-neutral-900">
                      {t(criterion.labelKey)}
                    </span>
                    <span className="type-body-sm block text-neutral-500">
                      {t(criterion.thresholdKey)}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <p className={hintClasses}>{t(k.calcMarkersHint)}</p>
          </fieldset>
        </div>

        <div className="mt-6 flex items-center gap-4">
          {/*
            `buttonVariants` on a bare <button> rather than <Button>: the
            idiom the other calculators use, and it keeps `data-testid` off a
            custom component's prop type.
          */}
          <button
            type="submit"
            data-testid="mafld-submit"
            disabled={measurements === null}
            className={cn(buttonVariants({ variant: "primary" }), "flex-1")}
          >
            {t(k.calcSubmit)}
          </button>
          {hasInput && (
            <button
              type="button"
              onClick={reset}
              className="type-title-sm shrink-0 cursor-pointer text-neutral-500 underline underline-offset-4 transition-colors hover:text-neutral-900"
            >
              {t(k.calcReset)}
            </button>
          )}
        </div>

        {/* What the definition is and is not, next to the button rather than
            buried further down the page: a reader is about to be told
            whether a named condition applies to them, and that it says
            nothing about severity has to arrive before the verdict, not
            after. */}
        <p className={hintClasses}>{t(k.calcEligibilityNote)}</p>

        {/*
          The refusal message used to be the result panel's status line. With
          that panel gone until Calculate succeeds, it belongs beside the
          control that produced it. `data-state` still names WHICH refusal is
          showing, so a test can tell them apart without asserting copy in
          three languages — and there is no longer an "empty" value for a
          green-looking spec to pass on by accident.
        */}
        {problemMessage !== null && (
          <p
            data-testid="mafld-status"
            data-state={problem}
            className="type-body-md text-terracotta-700 mt-4 text-center"
          >
            {problemMessage}
          </p>
        )}
      </form>

      {/* ── Result ── */}
      {/*
        The live region is mounted from the start so the panel is
        announced when it arrives; the panel itself only exists once
        Calculate has produced a figure, which is the point of the
        layout.
      */}
      <div aria-live="polite">
        {result !== null && fliStyles !== null && (
          <div
            data-testid="mafld-result"
            className="mt-8 rounded-[16px] bg-white p-6 md:p-8"
          >
            <div>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="type-label-md text-neutral-500 normal-case">
                    {t(k.calcResultLabel)}
                  </p>
                  {/*
                    `data-outcome` carries the verdict ID beside the translated
                    label. The four labels share no substring across en/de/fr
                    ("Criteria met", "Kriterien erfüllt", "Critères remplis"), so
                    a text regex cannot check the verdict in every locale the page
                    serves, and asserting the English wording would red the whole
                    suite on a translation pass.
                  */}
                  <p
                    data-testid="mafld-outcome"
                    data-outcome={result.outcome}
                    className={cn(
                      "type-title-lg mt-2 inline-block rounded-full px-4 py-1.5",
                      OUTCOME_STYLES[result.outcome].chip
                    )}
                  >
                    {t(MAFLD_OUTCOMES[result.outcome].labelKey)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="type-label-md text-neutral-500 normal-case">
                    {t(k.calcFliLabel)}
                  </p>
                  {/* No unit after the number: the index is a rescaled
                      likelihood on a 0 to 100 scale, not a percentage, and a
                      percent sign here would be a claim the paper does not
                      make. `data-band` so a test can check the band without the
                      label. */}
                  <p
                    data-testid="mafld-fli"
                    data-band={result.fliBand}
                    className={cn(
                      "type-display-md mt-1 tabular-nums",
                      fliStyles.accent
                    )}
                  >
                    {formatDecimal(result.fli, 1, locale)}
                  </p>
                </div>
              </div>

              {/* The BMI the reader's height and weight produced, shown because
                  it is the one input the form does not ask for directly and
                  because from 25 upwards it is what carried the metabolic half
                  for most readers. */}
              <p
                data-testid="mafld-bmi"
                className="type-body-sm mt-3 text-neutral-500"
              >
                {t(k.calcBmiLabel)} {formatDecimal(result.bmi, 1, locale)}
              </p>

              {/* Scale */}
              <div className="mt-6">
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcThresholdsLabel)}
                </p>
                <div className="relative mt-3">
                  {/*
                    Contiguous, no gap between segments. A gap would push every
                    boundary right of its true position while the marker is
                    placed on the ungapped index, so the marker would stop landing
                    on the band it names. Rounding lives on the clipping container
                    instead of the individual segments.
                  */}
                  <div className="flex h-2 w-full overflow-hidden rounded-full">
                    {segments.map((segment) => (
                      <div
                        key={segment.id}
                        className={cn(
                          "h-full",
                          FLI_BAND_STYLES[segment.id].bar
                        )}
                        style={{
                          width: `${(segment.span / (SCALE_MAX - SCALE_MIN)) * 100}%`,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    aria-hidden
                    className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-neutral-900 bg-white transition-[left] duration-300 ease-out"
                    style={{ left: `${positionOf(result.fli)}%` }}
                  />
                </div>
                {/*
                  Ticks are absolutely positioned at their real place on the axis.
                  The two cut-offs are not evenly spaced on it (30 sits at 30% of
                  the 0 to 100 axis and 60 at 60%), so a flex justify-between row
                  would print them at even intervals and mislabel the bands
                  underneath.
                */}
                <div aria-hidden className="relative mt-3 h-4">
                  <span className="type-label-sm absolute top-0 left-0 text-neutral-400 normal-case tabular-nums">
                    {formatDecimal(SCALE_MIN, 0, locale)}
                  </span>
                  {[FLI_RULES_OUT_BELOW, FLI_RULES_IN_FROM].map((value) => (
                    <span
                      key={value}
                      className="type-label-sm absolute top-0 -translate-x-1/2 text-neutral-400 normal-case tabular-nums"
                      style={{ left: `${positionOf(value)}%` }}
                    >
                      {formatDecimal(value, 0, locale)}
                    </span>
                  ))}
                  <span className="type-label-sm absolute top-0 right-0 text-neutral-400 normal-case tabular-nums">
                    {formatDecimal(SCALE_MAX, 0, locale)}
                  </span>
                </div>
                <p className="type-body-sm mt-2 text-neutral-500">
                  {t(k.calcScaleCaption)}
                </p>
              </div>

              <p
                data-testid="mafld-meaning"
                className="type-body-md mt-6 border-t border-neutral-200 pt-5 text-neutral-600"
              >
                {t(MAFLD_OUTCOMES[result.outcome].meaningKey)}
              </p>

              {/* Shown whenever the reader answered the imaging question either
                  way, so a verdict resting on their own scan says so. Without
                  it the index sitting in the corner reads as the thing that
                  decided, which on a `confirmed` answer with a low index would
                  look like the page contradicting itself. */}
              {(result.steatosis === "confirmed" ||
                result.steatosis === "excluded") && (
                <p
                  data-testid="mafld-scan-note"
                  className="type-body-sm mt-4 text-neutral-500"
                >
                  {t(k.calcSteatosisScanNote)}
                </p>
              )}

              {/* Which of the three routes carried the metabolic half, from the
                  assessment's own list rather than re-derived here. Every route
                  that fires is named, not just the first: a reader with a BMI of
                  33 and type 2 diabetes who is told only about their weight
                  would reasonably conclude that losing it settles the matter. */}
              <div className="mt-6 border-t border-neutral-200 pt-5">
                {result.limbs.length > 0 ? (
                  <>
                    <p className="type-label-md text-neutral-500 normal-case">
                      {t(k.calcLimbsLabel)}
                    </p>
                    <ul data-testid="mafld-limbs" className="mt-2 space-y-1">
                      {result.limbs.map((limb) => (
                        <li
                          key={limb}
                          data-limb={limb}
                          className="type-body-md text-neutral-900"
                        >
                          {t(MAFLD_LIMBS[limb].labelKey)}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p
                    data-testid="mafld-no-limbs"
                    className="type-body-md text-neutral-600"
                  >
                    {t(k.calcNoLimbsNote)}
                  </p>
                )}
              </div>

              {/* The six markers the reader meets, shown whatever route carried
                  them. Informative rather than load-bearing outside the lean
                  route, and that is the point: a reader who met the definition
                  on BMI alone still learns which of the six they carry. */}
              <div className="mt-5">
                {result.criteriaMet.length > 0 ? (
                  <>
                    <p className="type-label-md text-neutral-500 normal-case">
                      {t(k.calcCriteriaMetLabel)}
                    </p>
                    <ul
                      data-testid="mafld-criteria"
                      className="mt-2 flex flex-wrap gap-2"
                    >
                      {result.criteriaMet.map((criterion) => (
                        <li
                          key={criterion}
                          data-criterion={criterion}
                          className="type-body-sm rounded-full bg-neutral-100 px-3 py-1 text-neutral-700"
                        >
                          {t(MAFLD_CRITERIA[criterion].labelKey)}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p
                    data-testid="mafld-no-criteria"
                    className="type-body-sm text-neutral-500"
                  >
                    {t(k.calcCriteriaNoneNote)}
                  </p>
                )}
              </div>

              {/* The one honest consequence of using checkboxes: a lean reader
                  with no ticks may simply not have checked. Shown only when the
                  third route was their only way through and it did not fire on
                  the answers given, so it never appears beside a verdict it
                  cannot change. */}
              {result.leanUnanswered && (
                <p
                  data-testid="mafld-lean-note"
                  className="bg-terracotta-50 type-body-sm mt-5 rounded-lg p-4 text-neutral-600"
                >
                  {t(k.calcLeanUnansweredNote)}
                </p>
              )}

              <div className="mt-6 border-t border-neutral-200 pt-5">
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcNextLabel)}
                </p>
                <p
                  data-testid="mafld-next"
                  className="type-body-md mt-2 text-neutral-600"
                >
                  {t(MAFLD_OUTCOMES[result.outcome].nextKey)}
                </p>
              </div>
            </div>
            {/*
              Check-up hand-off. Inside the result panel and gated on `result`, so
              it is the reward for pressing Calculate rather than another CTA
              sitting on the page from the start. It books the market's blood
              check-up directly (CalculatorBookingCta) rather than sending the
              reader through the quiz first, and it does NOT claim the booking
              reads this result.
            */}
            <div className="bg-aubergine-50 mt-6 rounded-lg p-5">
              <h3 className="type-title-md text-neutral-900">
                {t(k.quizHeading)}
              </h3>
              <p className="type-body-md mt-2 text-neutral-600">
                {t(k.quizBody)}
              </p>
              <CalculatorBookingCta calculator="mafld">
                {t(k.quizCta)}
              </CalculatorBookingCta>
            </div>
          </div>
        )}
      </div>

      {/* The caveat is about the tool, not the answer, so it stays on
          the page whether or not anything has been calculated. */}
      <p className="type-body-sm mx-auto mt-8 max-w-[600px] text-center text-neutral-500">
        {t(k.calcDisclaimer)}
      </p>
    </div>
  )
}
